import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as AskAiIcon } from "../../assets/icons/ask_ai.svg";
import { ReactComponent as BookmarkIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as HighlightIcon } from "../../assets/icons/highlight.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info.svg";
import { ReactComponent as NotebookIcon } from "../../assets/icons/notebook.svg";
import { ReactComponent as PagesIcon } from "../../assets/icons/pages.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as SpeechIcon } from "../../assets/icons/speech.svg";
import { ReactComponent as UnderlineIcon } from "../../assets/icons/underline.svg";
import { ReactComponent as UndoIcon } from "../../assets/icons/undo.svg";
import { ReactComponent as ZoomInIcon } from "../../assets/icons/zoomIn.svg";
import { useIsMobile, useIsTouchTablet } from "../../hooks/useMediaQuery";
import { getAltLabel, getCtrlLabel } from "../../utils/platformUtils";
import "./Help.css";

const Help = ({ show, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("features");
  const isMobile = useIsMobile();
  const isTouchTablet = useIsTouchTablet();
  const showShortcuts = !isMobile && !isTouchTablet;
  const showGestures = isMobile || isTouchTablet;
  // Temporarily hide the shortcuts/gestures tab UI — keep content present but not shown
  const showShortcutsTab = false;

  // Platform-aware modifier labels
  const ctrl = useMemo(() => getCtrlLabel(), []);
  const alt = useMemo(() => getAltLabel(), []);

  useEffect(() => {
    if (!showShortcutsTab && activeTab === "shortcuts") {
      setActiveTab("features");
    }
  }, [activeTab, showShortcutsTab]);

  if (!show) {
    return null;
  }

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>{t("help.title")}</h2>
          <button
            onClick={onClose}
            className="close-button"
            aria-label={t("help.close")}
            title={t("help.close")}
          >
            &times;
          </button>
        </div>

        <div className="help-tabs">
          <button
            className={`help-tab ${activeTab === "features" ? "active" : ""}`}
            onClick={() => setActiveTab("features")}
          >
            {t("help.featuresTab")}
          </button>
          {showShortcutsTab && (
            <button
              className={`help-tab ${activeTab === "shortcuts" ? "active" : ""}`}
              onClick={() => setActiveTab("shortcuts")}
            >
              {t(showShortcuts ? "help.shortcutsTab" : "help.gesturesTab")}
            </button>
          )}
        </div>

        <div className="help-modal-content">
          {activeTab === "features" && (
            <div className="help-tab-content">
              {/* Reading & Navigation */}
              <div className="feature-section">
                <h3 className="section-title">{t("help.readingNav")}</h3>
                <div className="help-feature-card">
                  <PagesIcon className="card-icon" />
                  <h4>{t("help.navigation")}</h4>
                  <p>{t("help.navigationDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <ZoomInIcon className="card-icon" />
                  <h4>{t("help.zoomControls")}</h4>
                  <p>{t("help.zoomControlsDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <SearchIcon className="card-icon" />
                  <h4>{t("help.search")}</h4>
                  <p>{t("help.searchDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <EyeIcon className="card-icon card-icon--artboard" />
                  <h4>{t("toolbar.display")}</h4>
                  <p>{t("help.displayDesc")}</p>
                </div>
              </div>

              {/* Organization */}
              <div className="feature-section">
                <h3 className="section-title">{t("help.organization")}</h3>
                <div className="help-feature-card">
                  <BookmarkIcon className="card-icon" />
                  <h4>{t("help.bookmarks")}</h4>
                  <p>{t("help.bookmarksDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <NotebookIcon className="card-icon card-icon--artboard card-icon--notebook" />
                  <h4>{t("notes.title")}</h4>
                  <p>{t("help.notesDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <InfoIcon className="card-icon card-icon--artboard" />
                  <h4>{t("rightToolbar.info")}</h4>
                  <p>{t("help.publicationDesc")}</p>
                </div>
              </div>

              {/* Annotation Tools */}
              <div className="feature-section">
                <h3 className="section-title">{t("help.annotationTools")}</h3>
                <div className="help-feature-card">
                  <HighlightIcon className="card-icon" />
                  <h4>{t("help.highlights")}</h4>
                  <p>{t("help.highlightsDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <UnderlineIcon className="card-icon" />
                  <h4>{t("common.underline")}</h4>
                  <p>{t("help.underlineDesc")}</p>
                </div>

                <div className="help-feature-card">
                  <UndoIcon className="card-icon" />
                  <h4>{t("help.undoRedo")}</h4>
                  <p>{t("help.undoRedoDesc")}</p>
                </div>
              </div>

              {/* Reading Assistant / AI */}
              <div className="feature-section">
                <h3 className="section-title">{t("readingAssistant.title")}</h3>
                <div className="help-feature-card">
                  <AskAiIcon className="card-icon card-icon--artboard preserve-original-fill" />
                  <h4>{t("readingAssistant.tabs.summary")}</h4>
                  <p>{t("readingAssistant.summary.welcome")}</p>
                </div>

                <div className="help-feature-card">
                  <AskAiIcon className="card-icon card-icon--artboard preserve-original-fill" />
                  <h4>{t("readingAssistant.tabs.askAi")}</h4>
                  <p>{t("readingAssistant.askAi.empty")}</p>
                </div>

                <div className="help-feature-card">
                  <AskAiIcon className="card-icon card-icon--artboard preserve-original-fill" />
                  <h4>{t("readingAssistant.tabs.quiz")}</h4>
                  <p>{t("readingAssistant.quiz.subtitle")}</p>
                </div>

                <div className="help-feature-card">
                  <AskAiIcon className="card-icon card-icon--artboard preserve-original-fill" />
                  <h4>{t("contextMenu.translate")}</h4>
                  <p>{t("help.translateDesc")}</p>
                </div>
              </div>

              {/* Coming Soon */}
              <div className="feature-section">
                <h3 className="section-title">{t("help.comingSoon")}</h3>
                <div className="help-feature-card disabled">
                  <SpeechIcon className="card-icon" />
                  <h4>{t("help.audioReading")}</h4>
                  <p>{t("help.audioReadingDesc")}</p>
                </div>
              </div>
            </div>
          )}

          {showShortcutsTab && activeTab === "shortcuts" && (
            <div className="help-tab-content shortcuts-layout">
              {showShortcuts ? (
                <>
                  <div className="shortcut-group">
                    <h4>{t("help.shortcuts.navigationAccessibility")}</h4>
                    <div className="shortcut-list">
                      <div className="shortcut-item">
                        <span className="shortcut-key">Tab</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.browseForward")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">Shift + Tab</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.browseBackward")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">Enter</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.activateButton")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">Spacebar</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.selectToggle")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">Esc</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.closeDialogs")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">↑ / ↓</span>
                        <span className="shortcut-desc">
                          {t("help.gestures.swipeUpDownDesc")}: PDF
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">→ / ←</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.nextPrevPage")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">Home</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.jumpTop")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">End</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.jumpBottom")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="shortcut-group">
                    <h4>{t("help.shortcuts.zoomViewControls")}</h4>
                    <div className="shortcut-list">
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + +</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.zoomIn")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + -</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.zoomOut")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + \</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.fitWidth")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + /</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.fitPage")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="shortcut-group">
                    <h4>{t("help.shortcuts.editingAnnotations")}</h4>
                    <div className="shortcut-list">
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + Z</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.undo")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + Y</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.redo")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{alt} + H</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.toggleHighlight")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">N</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.toggleNotes")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">B</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.toggleBookmarks")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">T</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.openToc")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + S</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.saveBookmark")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="shortcut-group">
                    <h4>{t("help.shortcuts.searchContent")}</h4>
                    <div className="shortcut-list">
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + F</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.searchWithin")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + C</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.copyText")}
                        </span>
                      </div>
                      <div className="shortcut-item">
                        <span className="shortcut-key">{ctrl} + A</span>
                        <span className="shortcut-desc">
                          {t("help.shortcuts.selectAll")}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="shortcut-group">
                  <h4>{t("help.gestures.title")}</h4>
                  <div className="shortcut-list">
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.tap")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.gestures.tapDesc")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.swipeLeft")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.gestures.swipeLeftDesc")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.swipeRight")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.gestures.swipeRightDesc")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.swipeUpDown")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.gestures.swipeUpDownDesc")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.pinchOut")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.shortcuts.zoomIn")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.pinchIn")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.shortcuts.zoomOut")}
                      </span>
                    </div>
                    <div className="shortcut-item">
                      <span className="shortcut-key">
                        {t("help.gestures.longPress")}
                      </span>
                      <span className="shortcut-desc">
                        {t("help.gestures.longPressDesc")}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help;
